import { useEffect, useRef, useState } from 'react';

/**
 * Carica Google Identity Services e disegna il bottone ufficiale.
 *
 * Lo script arriva da Google e non da un bundle nostro: è un requisito loro,
 * ed è anche l'unico modo perché il bottone resti conforme alle loro linee
 * guida di marchio senza rincorrerle a mano.
 *
 * La logica sta qui e non nel componente perché è tutta effetti e stato di
 * caricamento — il componente deve solo avere un posto dove Google disegna.
 */

const SCRIPT_ID = 'google-identity-services';
const SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

interface IGoogleAccounts {
  accounts: {
    id: {
      initialize: (config: {
        client_id: string;
        callback: (response: { credential?: string }) => void;
      }) => void;
      renderButton: (parent: HTMLElement, options: Record<string, unknown>) => void;
    };
  };
}

/** Lo script si aggiunge una volta sola: due login page lo chiederebbero due volte. */
const loadScript = (): Promise<void> =>
  new Promise((resolve, reject) => {
    if (document.getElementById(SCRIPT_ID)) {
      // Già presente ma forse non ancora eseguito: `google` esiste solo dopo.
      if ((window as unknown as { google?: IGoogleAccounts }).google) return resolve();
      document
        .getElementById(SCRIPT_ID)!
        .addEventListener('load', () => resolve(), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Script Google non raggiungibile'));
    document.head.appendChild(script);
  });

interface IUseGoogleSignInParams {
  /** Riceve l'ID token firmato da Google. NON va letto qui: lo verifica il server. */
  onCredential: (idToken: string) => void;
  /**
   * Larghezza in px. Se non la passi, si misura il contenitore: Google
   * Identity Services non accetta percentuali, quindi un numero fisso
   * scritto a mano resta largo uguale mentre la card cambia — ed è così che
   * il bottone finiva più stretto di quello sopra, centrato di sbieco.
   */
  width?: number;
}

/** Massimo accettato da GIS: oltre, il bottone non viene reso affatto. */
const GIS_MAX_WIDTH = 400;
/** Ripiego se il contenitore non ha ancora una larghezza misurabile. */
const FALLBACK_WIDTH = 320;

export const useGoogleSignIn = ({ onCredential, width }: IUseGoogleSignInParams) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  /*
    `onCredential` in un ref e non nelle dipendenze: la callback cambia
    identità a ogni render della pagina di login, e rimetterla fra le
    dipendenze rimonterebbe il bottone di Google a ogni battuta di tasto nel
    campo email.
  */
  const callbackRef = useRef(onCredential);
  callbackRef.current = onCredential;

  useEffect(() => {
    if (!clientId) {
      setError('Accesso con Google non configurato');
      return;
    }

    let annullato = false;

    loadScript()
      .then(() => {
        const google = (window as unknown as { google?: IGoogleAccounts }).google;
        if (annullato || !google || !containerRef.current) return;

        google.accounts.id.initialize({
          client_id: clientId,
          callback: (response) => {
            if (response.credential) callbackRef.current(response.credential);
          },
        });

        // Misurata al momento del disegno, non prima: il contenitore ha una
        // larghezza solo dopo che la card è stata impaginata.
        const misurata = Math.round(containerRef.current.offsetWidth);
        const larghezza = Math.min(
          GIS_MAX_WIDTH,
          width ?? (misurata > 0 ? misurata : FALLBACK_WIDTH),
        );

        google.accounts.id.renderButton(containerRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'signin_with',
          shape: 'pill',
          locale: 'it',
          logo_alignment: 'center',
          width: larghezza,
        });
      })
      .catch(() => {
        if (!annullato) setError('Accesso con Google non disponibile');
      });

    return () => {
      annullato = true;
    };
  }, [clientId, width]);

  return { containerRef, error, isConfigured: !!clientId };
};
